import React, { useRef, useState, useEffect, useContext } from "react";
import { DialogType, Stack, Spinner, IContextualMenuProps, TooltipHost, DirectionalHint } from "@fluentui/react";
import { ShieldLockRegular, Attach12Regular, NotepadEdit16Regular, SquareRegular } from "@fluentui/react-icons";
import { VERSION, MAX_FILE_SIZE, MAX_FILE_COUNT } from "../../constants/constants";

import uuid from 'react-uuid';
import { isEqual } from "lodash-es";

import styles from "./Chat.module.css";

import { AISearchRAGCitation_Parsed, Citation_Parsed, isAISearchRAGCitation } from "../../api/citations";

import {
    ChatMessage,
    ConversationRequest,
    ToolMessageContent,
    ChatResponse,
    Conversation,
    conversationGenerateStart,
    historyRead,
    ChatHistoryLoadingState,
    CosmosDBStatus,
    checkMessage,
    ErrorMessage,
    getCompanyIcon,
    getConversation,
    historyDelete,
    abortConversation,
    conversationStream,
    GeneratingConversationInfo
} from "../../api";
import { Answer } from "../../components/Answer";
import { QuestionInput } from "../../components/QuestionInput";
import { AppStateContext } from "../../state/AppProvider";
import { useBoolean } from "@fluentui/react-hooks";
import { MessageDialog } from "../../components/common/Dialog";
import FileDisplay from "../../components/Answer/FileDisplay";
import { MinutesTranscriptionDialog } from "../../components/MinutesTranscription/MinutesTranscription";
import { useNavigate, useParams } from "react-router-dom";
import { CitationPanel } from "./CitationPanel";
import { MobileCitationPanel } from "./MobileCitationPanel";
import { useIsMobile } from "../../hooks/useIsMobile";
import { ErrorMessage as ErrorMessageElement, UserFileMessage, UserMessage } from "../../components/Answer/Messages";
import { SideBar } from "../sidebar/Sidebar";
import { ChatActionButton } from "../../components/common/Button";
import { FileIcon } from "../../components/common/FileIcon";

interface FileInfo {
  name: string;
  type: string;
}

const Chat = () => {
    const appStateContext = useContext(AppStateContext);

    const { conversationId: thisConvId } = useParams<{ conversationId: string }>();
    const thisConvIdRef = useRef(thisConvId);
    thisConvIdRef.current = thisConvId;

    const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);
    const [isChatLoading, setIsChatLoading] = useState<boolean>(false);
    // 履歴から開いた会話をDBから読み直している間の会話ID。
    // 読み直しの完了前に送信すると、読み直した内容で送信した質問が上書きされて消えるため、その間は送信を受け付けない。
    // A→B→A と素早く切り替えた場合に、最初の A の読み直しの完了で2回目の A の読み直し中の状態を解除しないよう、読み直しごとの連番で判定する
    const [syncing, setSyncing] = useState<{ convId: string, seq: number } | undefined>(undefined);
    const syncSeqRef = useRef(0);
    const syncingConvId = syncing?.convId;
    const syncingConvIdRef = useRef<string | undefined>(undefined);
    syncingConvIdRef.current = syncingConvId;
    const [activeCitation, setActiveCitation] = useState<AISearchRAGCitation_Parsed|undefined>();
    const [isCitationPanelOpen, setIsCitationPanelOpen] = useState<boolean>(false);
    const [showAuthMessage, setShowAuthMessage] = useState<boolean>(true);
    const [messages, setMessages] = useState<ChatMessage[]>([])
    const [checkingConvIds, setCheckingConvIds] = useState<Set<string|undefined>>(new Set());
    const [hideMessageDialog, { toggle: toggleMessageDialog }] = useBoolean(true);
    const [dialogMsg, setMessageDialog] = useState<ErrorMessage | null>()
    const [showCancelButton, setShowCancelButton] = useState<boolean>(false);
    const [dialogHandlers, setDialogHandlers] = useState({ handleOk: () => {}, handleCancel: () => {} });
    const [imageUrl, setImageUrl] = useState<string | undefined>();
    const [files, setFiles] = useState<File[]>([]);
    const [filesInfo, setFilesInfo] = useState<FileInfo[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isDragOver, setIsDragOver] = useState<boolean>(false);
    const [isTranscriptionOpen, setIsTranscriptionOpen] = useState<boolean>(false);
    // Blob用のキャッシュ本体
    // 非同期処理中も最新の値を参照する必要があるのでrefで
    const urlBlobMap = useRef(new Map<string, string>())
    const loadingBlobs = useRef(new Set<string>())
    // キャッシュ再レンダリング用、refは変更しても再レンダリングが走らないので
    const [urlBlobMap_render, setUrlBlobMap_render] = useState(new Map<string, string>())
    const [loadingBlobs_render, setLoadingBlobs_render] = useState(new Set<string>())
    const isConvDifferentUsers = (Boolean(appStateContext?.state.loginUserInfo?.id) && Boolean(appStateContext?.state.currentChat?.user_id)) && (appStateContext?.state.loginUserInfo?.id != appStateContext?.state.currentChat?.user_id)

    const navigate = useNavigate()

    const messageDialogContentProps = {
        type: DialogType.normal,
        title: dialogMsg?.title,
        closeButtonAriaLabel: 'Close',
        subText: dialogMsg?.subtitle,
    };

    const ROLES = ["user", "assistant", "tool"]
    const [USER, ASSISTANT, TOOL] = ROLES

    const isMobile = useIsMobile();

    const generatingConversations = appStateContext?.state.generatingConversations ?? new Map<string, GeneratingConversationInfo>()
    const setGeneratingConvIds = (f: (v: Map<string, GeneratingConversationInfo>) => Map<string, GeneratingConversationInfo>) => {
        const value = f(appStateContext?.state.generatingConversations ?? new Map());
        appStateContext?.dispatch({type: "UPDATE_GENERATING_CONVERSATIONS", payload: value})
    }

    const [generatingMessages, setGeneratingMessages] = useState(new Map<string, ChatMessage[]>()) // conv_id -> messages
    // 永続化済みメッセージ(messages)にストリーム中メッセージ(generatingMessages)をID基準で重ねる。
    // 同一IDは上書き(ライブ側が新しい)、新規IDは末尾へ追加。これにより完了境界での二重表示を防ぐ。
    const mergeMessagesById = (base: ChatMessage[], overlay: ChatMessage[]): ChatMessage[] => {
        const result = [...base]
        const indexById = new Map<string, number>()
        result.forEach((m, i) => { if (m.id) indexById.set(m.id, i) })
        for (const m of overlay) {
            const existing = m.id !== undefined ? indexById.get(m.id) : undefined
            if (existing !== undefined) {
                result[existing] = m
            } else {
                if (m.id) indexById.set(m.id, result.length)
                result.push(m)
            }
        }
        return result
    }
    const mergedMessages = mergeMessagesById(messages, generatingMessages.get(thisConvId ?? "") ?? [])

    const isCheckingThisConv = checkingConvIds.has(thisConvId);
    const isGeneratingThisConv = thisConvId !== undefined && Array.from(generatingConversations.values()).some((v) => v.history_metadata.conversation_id === thisConvId);
    const thisTaskId = thisConvId && Array.from(generatingConversations.entries()).find(([_, cnv]) => cnv.history_metadata.conversation_id === thisConvId)?.[0]

    // 生成中会話のストリームを二重に張らないための管理（アタッチ済みtask_id）
    const attachedTaskIds = useRef<Set<string>>(new Set())
    // 非同期のstream完了時に最新のchatHistoryを参照するためのref
    const chatHistoryRef = useRef(appStateContext?.state.chatHistory)
    chatHistoryRef.current = appStateContext?.state.chatHistory
    // 同上。表示中会話のメッセージ実体はcurrentChatが保持する（chatHistoryはhistoryList由来で空のことがある）
    const currentChatRef = useRef(appStateContext?.state.currentChat)
    currentChatRef.current = appStateContext?.state.currentChat

    const addCheckingConvId = (convId: string | undefined) => {
        setCheckingConvIds((v) => {
            v.add(convId);
            return new Set(v);
        })
    }
    const deleteCheckingConvId = (convId: string | undefined) => {
        setCheckingConvIds((v) => {
            v.delete(convId)
            return new Set(v);
        })
    }
    const addGeneratingConvId = (taskId: string, conv: GeneratingConversationInfo) => {
        setGeneratingConvIds((v) => {
            v.set(taskId, conv);
            return new Map(v);
        })
    }
    const deleteGeneratingConvId = (taskId: string, conv: GeneratingConversationInfo) => {
        setGeneratingConvIds((v) => {
            v.delete(taskId)
            return new Map(v);
        })
        setGeneratingMessages((v) => {
            v.delete(conv.history_metadata.conversation_id)
            return new Map(v)
        })
    }

    const isCheckingOrLoadingThisConv = isCheckingThisConv || isGeneratingThisConv
    const isSyncingThisConv = syncingConvId !== undefined && syncingConvId === thisConvId

    // Blobキャッシュ管理
    useEffect(() => {
        const citation_url = activeCitation?.url
        const file_path = activeCitation?.filepath
        if(citation_url && !urlBlobMap.current.has(citation_url) && !loadingBlobs.current.has(citation_url) && file_path?.toLowerCase().endsWith(".pdf") && activeCitation.page) {
            loadingBlobs.current.add(citation_url)
            setLoadingBlobs_render(new Set(loadingBlobs.current))
            fetch(citation_url, {method: "GET"})
            .then((res) => {
                if (res.status !== 200) {
                    loadingBlobs.current.delete(citation_url)
                    setLoadingBlobs_render(new Set(loadingBlobs.current))
                    return null
                }
                return res.blob()
            })
            .then((v) => {
                if (v) {
                    const blobUrl = URL.createObjectURL(v)
                    if (!urlBlobMap.current.has(citation_url)){
                        urlBlobMap.current.set(citation_url, blobUrl)
                        setUrlBlobMap_render(new Map(urlBlobMap.current))
                    }
                }
                loadingBlobs.current.delete(citation_url)
                setLoadingBlobs_render(new Set(loadingBlobs.current))
            })
        }
    }, [activeCitation?.url])

    // 異なる会話が選択されたときに引用を消す
    useEffect(() => {
        setActiveCitation(undefined)
    }, [mergedMessages.at(0)?.id])

    // URLパラメータからconversationIdを読み込んで会話履歴を取得
    useEffect(() => {
        if (!thisConvId) {
            appStateContext?.dispatch({type: "UPDATE_CURRENT_CHAT", payload: null })
            return
        }
        const chatInHistory = appStateContext?.state.chatHistory?.find((v) => v.id == thisConvId)
        if(chatInHistory && 0 < chatInHistory?.messages.length) {
            appStateContext?.dispatch({type: "UPDATE_CURRENT_CHAT", payload: chatInHistory })
        }
        else{
            setIsChatLoading(true)
        }
        const fetchConv = async () => {
            const syncSeq = ++syncSeqRef.current
            setSyncing({ convId: thisConvId, seq: syncSeq })
            try{
                const thisConv = await getConversation(thisConvId)
                if (!thisConv) {
                    await showDialog("エラー", "チャットが取得できません。権限が不足しているか、チャットが存在しない可能性があります。", false);
                    navigate("/")
                    return
                }
                const mes = await historyRead(thisConvId)

                // Cosmos DBのラグを考慮、historyReadによって最新が得られるとは限らない
                if (chatInHistory && mes.length < chatInHistory.messages.length) {
                    thisConv.messages = chatInHistory.messages
                }
                else {
                    thisConv.messages = mes
                }

                if (chatInHistory) {
                    appStateContext?.dispatch({type: "UPDATE_CHAT_HISTORY", payload: thisConv })
                }
                const isRagEnabled = appStateContext?.state.isRAGEnabled ?? false
                const thisRag = new Set(thisConv.rag_ids ?? [])
                if ((isRagEnabled && thisRag.size == 0) || (!isRagEnabled && thisRag.size != 0)) {
                    appStateContext?.dispatch({type: "SET_IS_RAG_ENABLED", payload: {isEnabled: !isRagEnabled, openRAGMenuIfToggleToOpen: true} })
                }
                if (!isEqual(appStateContext?.state.selectedRAGIds, thisRag)) {
                    appStateContext?.dispatch({type: "SET_RAG_CHAT", payload: thisRag})
                }
                appStateContext?.dispatch({type: "UPDATE_CURRENT_CHAT", payload: thisConv })
            } finally {
                setSyncing((v) => v?.seq === syncSeq ? undefined : v)
                setTimeout(() => {
                    setIsChatLoading(false)
                }, 100)
            }
        }
        fetchConv()
    }, [thisConvId]);

    useEffect(() => {
        if(appStateContext?.state.isCosmosDBAvailable?.status === CosmosDBStatus.NotWorking && appStateContext.state.chatHistoryLoadingState === ChatHistoryLoadingState.Fail && hideMessageDialog){
            let subtitle = `${appStateContext.state.isCosmosDBAvailable.status}. サイト管理者に連絡してください。`
            showDialog("チャット履歴が無効です。", subtitle, false)
        }
    }, [appStateContext?.state.isCosmosDBAvailable]);

    useEffect(() => {
       setIsChatLoading(appStateContext?.state.chatHistoryLoadingState === ChatHistoryLoadingState.Loading)
    }, [appStateContext?.state.chatHistoryLoadingState])

    const getUserInfoList = async () => {
        setShowAuthMessage(false);
    }

    const getLatestUserMessageIndex = (messages: ChatMessage[]) => {
        for (let i = messages.length - 1; i >= 0; i--) {
            if (messages[i].role === "user") {
                return i
            }
        }
    }

    const isLatestUserMessage = (messages: ChatMessage[], message: ChatMessage) => {
        const idx = getLatestUserMessageIndex(messages)
        if(idx === undefined) return false
        return messages[idx].id === message.id
    }

    const isFileMessage = (message: ChatMessage) => {
        return message.contentType === "file"
    }

    const currentModel = appStateContext?.state.selectedGPTModelIndex ? appStateContext?.state.availableModels.get(appStateContext.state.selectedGPTModelIndex) : undefined

    const tools = appStateContext?.state.tools ?? []
    const setTools = (value: string[]) => {
        const availableTools = currentModel?.tools.map(v=>v.name)
        const filtered = value.filter(v => availableTools?.includes(v))
        appStateContext?.dispatch({type: "SET_TOOLS", payload: filtered})
    }

    const [toolArgs, setToolArgsMap] = useState<Map<string, Map<string, any>>>(new Map())
    const setToolArgs = (toolName: string, argName: string, value: any) => {
        setToolArgsMap(prev => {
            const next = new Map(prev)
            const nextToolArgs = new Map(next.get(toolName) ?? new Map<string, any>())
            nextToolArgs.set(argName, value)
            next.set(toolName, nextToolArgs)
            return next
        })
    }

    const scrollChatToBottom = () => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" });
    };

    // メッセージダイアログ
    const showDialog = async (title: string, subtitle: string, showCancelButton: boolean) => {
        setShowCancelButton(showCancelButton)
        setMessageDialog({ title, subtitle })
        toggleMessageDialog();

        const dialogResponse = await new Promise((resolve) => {
            const handleOk = () => {
                resolve(true)
            }
            const handleCancel = () => {
                resolve(false)
            }
            setDialogHandlers({ handleOk, handleCancel })
        });
        toggleMessageDialog();
        return dialogResponse;
    }

    // 生成エラーの共通ハンドラ（generate開始・streamの両方で使用）
    const handleGenerationError = async (statusCode: number, message?: string, onRestore?: () => void, convTitle?: string, convId?:string) => {
        let convMes = ""
        if (convTitle) {
            convMes += `チャット「${convTitle}」`
        }
        if (convId) {
            convMes += `(ID: ${convId})`
        }
        if (convMes) {
            convMes += "\n\n"
        }
        if (statusCode === 401) {
            const dialogResponse = await showDialog("エラー", "タイムアウトしました。ログインしなおしてください。\n（OKを選択するとログイン画面に遷移します。Cancel押下時は直前に操作していた画面に戻ります。再度操作する場合はログアウトして再ログインしてください。）", true);
            if (dialogResponse) {
                navigate('/login/' + localStorage.getItem('WorQuforGAI_companykey'));
            } else {
                onRestore?.();
            }
            return;
        } else if (statusCode === 412) {
            await showDialog("エラー", "同一ユーザーが別端末でログインしました。\nログアウトします。", false);
            navigate('/login/' + localStorage.getItem('WorQuforGAI_companykey'));
            return;
        } else if (statusCode === 429) {
            await showDialog("エラー", convMes + "メッセージと添付ファイルのリクエスト数が制限を超えました。", false);
            return;
        } else if (statusCode === 500) {
            await showDialog("エラー", convMes + "サーバー内部でエラーが発生しました。\n\n" + (message ?? ""), false);
            return;
        } else if (statusCode === 200 && message) {
            // status 200だがerrorが含まれている場合
            if (message !== "aborted") {
                await showDialog("エラー", convMes + "回答の生成中にエラーが発生しました。\n\n" + message, false);
            }
            return;
        } else {
            await showDialog("エラー", convMes + (message ?? "回答の生成中にエラーが発生しました。問題が解決しない場合はサイト管理者に連絡してください。"), false);
            return;
        }
    }

    // stream受信中の進捗（conv_id単位で生成中メッセージを更新）
    const handleStreamProgress = (res: ChatResponse, convId: string) => {
        res.choices[0].messages.forEach((obj) => {
            obj.date = new Date().toISOString();
        })
        setGeneratingMessages((v) => {
            v.set(convId, res.choices[0].messages)
            return new Map(v)
        })
    }

    // stream完了時、chatHistoryの該当会話に結果メッセージをマージ
    const handleStreamComplete = (res: ChatResponse, convId: string) => {
        // 現在のチャットまたはチャット履歴の中の自分
        const currentThisChat = (currentChatRef.current?.id === convId ? currentChatRef.current : undefined) ?? chatHistoryRef.current?.find((v) => v.id === convId)

        // 自分がいない場合はここでメッセージを処理する必要は無い（表示しようとしたときに新たに読み込まれるので）
        if (currentThisChat === undefined) return;

        // 実体が空＝historyList由来の未読み込みエントリ。assistantだけ載せると
        // userメッセージ欠落の会話ができてしまうので、fetchConvのDB読み直しに委ねる
        if (currentThisChat.messages.length === 0) return;

        const resultConversation = currentThisChat
        const resMessages = res.choices[0].messages

        // baseに既に同一idのメッセージが含まれる場合は二重追加しない（防御的措置）。
        // fetchConv等がストリーム完了前にDB永続化済みメッセージを取り込むと、
        // 以下のappendと重複するため、idで排除する。
        const existingIds = new Set(resultConversation.messages.map((m) => m.id).filter(Boolean))
        const pushIfNew = (message: ChatMessage) => {
            if (message.id && existingIds.has(message.id)) return;
            if (message.id) existingIds.add(message.id);
            resultConversation.messages.push(message);
        }

        // ツールとアシスタント出力を処理
        for (const resultMessage of resMessages) {
            if (resultMessage.role === TOOL) {
                pushIfNew(resultMessage);
            }
        }
        for (const resultMessage of resMessages) {
            if (resultMessage.role === ASSISTANT) {
                pushIfNew(resultMessage);
            }
        }

        // AIが出力したファイルを処理
        if (res.create_file) {
            for (const fileMessage of res.create_file) {
                pushIfNew(fileMessage)
            }
        }
        appStateContext?.dispatch({ type: 'UPDATE_CHAT_HISTORY', payload: resultConversation });

        // 表示されているのがこの会話なら、現在のチャット表示を更新
        if (thisConvIdRef.current === convId) {
            appStateContext?.dispatch({ type: "UPDATE_CURRENT_CHAT", payload: { ...resultConversation } });
        }
    }

    // 指定task_idのstreamにアタッチし、受信完了まで待つ
    const attachStream = async (taskId: string, conv: GeneratingConversationInfo) => {
        try {
            await conversationStream(
                taskId,
                (statusCode, message) => handleGenerationError(statusCode, message, undefined, conv.history_metadata.title, conv.history_metadata.conversation_id),
                (res) => handleStreamProgress(res, conv.history_metadata.conversation_id),
                (res) => handleStreamComplete(res, conv.history_metadata.conversation_id),
            )
        } catch (e) {
            console.log(e)
        } finally {
            attachedTaskIds.current.delete(taskId)
            deleteGeneratingConvId(taskId, conv)
            if (thisConvIdRef.current === conv.history_metadata.conversation_id) {
                setTimeout(() => {
                    scrollChatToBottom();
                }, 100);
            }
        }
    }

    // generatingConvIds の増分を検知して、新規task_idのstreamを開始する
    useEffect(() => {
        for (const [taskId, convId] of generatingConversations.entries()) {
            if (attachedTaskIds.current.has(taskId)) continue;
            attachedTaskIds.current.add(taskId);
            void attachStream(taskId, convId);
        }
    }, [generatingConversations]);

    const makeApiRequestWithCosmosDB = async (
        conversation: Conversation | undefined,
        question: string,
        sendFiles: File[],
        ragIds?: Set<number>, 
        tools?: string[],
        toolArgs?: Map<string, Map<string, any>>,
        template_id?: number, 
    ) => {
        const userMessage: ChatMessage = {
            id: uuid(),
            role: "user",
            content: question,
            date: new Date().toISOString(),
        };

        // 現在生成中
        if(isCheckingOrLoadingThisConv) {
            await showDialog("エラー", "現在生成中です、生成完了までお待ちください。", false);
            setQuestion(question);
            return;
        }
        // 会話の読み直し中（テンプレートからの送信など、送信ボタン以外の経路のため）
        if(conversation?.id !== undefined && syncingConvIdRef.current === conversation.id) {
            await showDialog("エラー", "チャットを読み込み中です。読み込み完了後に再度送信してください。", false);
            setQuestion(question);
            return;
        }

        const toolArgSend = new Map(toolArgs);

        // RAGモード
        if(appStateContext?.state?.isRAGEnabled){
            // 1つ以上のカテゴリが選択されているか？
            if(ragIds?.size == 0){
                await showDialog("エラー", "文書検索カテゴリを選択してください。", false);
                setQuestion(question);
                return;
            }

            // documentSearchToolArgをマージ
            const documentSearchArg = appStateContext?.state.documentSearchToolArg;
            if (documentSearchArg) {
                // チェック
                for(const [rag_id, options] of documentSearchArg.tag_options) {
                    // そもそもカテゴリ選択されていないカテゴリの追加引数などどうでも良い
                    if (!ragIds?.has(Number(rag_id))) {
                        continue;
                    }
                    if (options.use_tags && options.tag_keys.length === 0) {
                        await showDialog("エラー", "サブカテゴリが選択されていません\nサブカテゴリによる絞り込みを行わない場合は、「絞り込みをしない」を選択してください。", false);
                        setQuestion(question);
                        return;
                    }
                }
                toolArgSend.set("DocumentSearch", new Map(Object.entries(documentSearchArg)))
            }
        }
        
        // メッセージの事前チェック
        addCheckingConvId(conversation?.id);
        const response = await checkMessage(question);
        if(response.level === 401){
            // エラー
            const dialogResponse = await showDialog("エラー", "タイムアウトしました。ログインしなおしてください。\n（OKを選択するとログイン画面に遷移します。Cancel押下時は直前に操作していた画面に戻ります。再度操作する場合はログアウトして再ログインしてください。）", true);
            deleteCheckingConvId(conversation?.id);
            if (dialogResponse) {
                // OKボタンが押された場合の処理
                navigate('/login/' + localStorage.getItem('WorQuforGAI_companykey'));
            }
            else {
                setQuestion(question);
            }
            return;
        }else if(response.level === 412){
            await showDialog("エラー", "同一ユーザーが別端末でログインしました。\nログアウトします。", false);
            deleteCheckingConvId(conversation?.id);
            navigate('/login/' + localStorage.getItem('WorQuforGAI_companykey'));
            return;
        }else if(response.level == -1){
            await showDialog("エラー", "メッセージチェックでエラーが発生しました", false);
            deleteCheckingConvId(conversation?.id);
            setQuestion(question);
            return;
        } else if(response.level == 2) {
            // エラー
            await showDialog("エラー", "使用できない文字列が含まれています。\n該当ワード : " + response.word, false);
            deleteCheckingConvId(conversation?.id);
            setQuestion(question);
            return;
        } else if(response.level == 1) {
            // メッセージダイアログを表示
            const dialogResponse = await showDialog("警告", "使用できない文字列が含まれています。\n処理を続行しますか？\n該当ワード : " + response.word, true);

            if (dialogResponse) {
                // OKボタンが押された場合の処理
            } else {
                deleteCheckingConvId(conversation?.id);
                setQuestion(question);
                return;
            }
        }

        // リクエストするメッセージ
        const reqMessages = [...(conversation?.messages ?? []), userMessage].filter((mes) => ROLES.includes(mes.role))
        const request: ConversationRequest = {
            messages: [...(conversation?.messages ?? []), userMessage].filter((mes) => ROLES.includes(mes.role))
        }
        setMessages(reqMessages)
        scrollChatToBottom();

        let generating_info: GeneratingConversationInfo|undefined = undefined
        const model_index = appStateContext?.state.selectedGPTModelIndex as string
        const rag_ids = ragIds ? Array.from(ragIds).sort() : []
        let resultConversation = conversation;

        try {
            const send_files = sendFiles
            // 添付ファイルクリア
            if ( files && files.length > 0 && send_files == files){
                files.forEach((file, index) => {
                    removeFile(index);
                });
            } 

            const onError = (statusCode: number, message?: string) =>
                handleGenerationError(statusCode, message, () => setQuestion(question), conversation?.title, conversation?.id);

            generating_info = await conversationGenerateStart(request, send_files, model_index, onError, conversation?.id, tools, toolArgSend, rag_ids, template_id)
            if (generating_info === undefined) {
                throw new Error()
            }

            deleteCheckingConvId(conversation?.id);
            addGeneratingConvId(generating_info.task_id, generating_info);
            const userReqProcessedMessages =  [...(conversation?.messages ?? []), ...generating_info.file_messages, userMessage].filter((mes) => ROLES.includes(mes.role))
            

            resultConversation = conversation ?? {
                id: generating_info.history_metadata.conversation_id,
                title: generating_info.history_metadata.title,
                messages: [],
                date: generating_info.history_metadata.date,
                rag_ids: rag_ids ?? [],
                user_id: appStateContext?.state.loginUserInfo?.id ?? ""
            }
            resultConversation.messages = userReqProcessedMessages
            
            appStateContext?.dispatch({ type: 'UPDATE_CHAT_HISTORY', payload: resultConversation });
            if (thisConvIdRef.current === undefined && conversation === undefined) {
                navigate(`/chat/${generating_info.history_metadata.conversation_id}`)
            }

            // streamへのアタッチはgeneratingConvIdsの増分を監視するuseEffectが行う
            // （このタブでの生成開始・別セッションからの復元を同一経路で処理する）

        } catch ( e )  {
            deleteCheckingConvId(conversation?.id);
            return;
        }
        return;
    }

    const newChat = () => {
        setIsCitationPanelOpen(false);
        setActiveCitation(undefined);
        appStateContext?.dispatch({type: "UPDATE_CURRENT_CHAT", payload: null })
        navigate(`/`)
        setMessages([])
        appStateContext?.dispatch({"type": "SET_PROMPT_TEMPLATE_OVERRIDE", "payload": null})
    };

    const stopGenerating = async (conv_task_id: string) => {
        await abortConversation(conv_task_id)
    }

    useEffect(() => {
        setMessages(appStateContext?.state.currentChat?.messages || [])
    }, [appStateContext?.state.currentChat, appStateContext?.state.currentChat?.id]);

    useEffect(() => {
        getUserInfoList();
    }, []);

    useEffect(() => {
        const fetchImage = async () => {
            try {
              const objectURL = await getCompanyIcon();
              setImageUrl(objectURL);
            } catch (error) {
              console.error('クライアント側アイコン取得例外エラー:', error);
            }
        };
        fetchImage();
      }, []);

    const onShowCitation = (citation: Citation_Parsed) => {
        if (isAISearchRAGCitation(citation)){
            setActiveCitation(citation);
            setIsCitationPanelOpen(true);
        }
    };

    const onViewSource = (citation: AISearchRAGCitation_Parsed) => {
        if (citation.url && !citation.url.includes("blob.core")) {
            window.open(citation.url, "_blank");
        }
    };

    const onEditSend = async (question: string, conv_id: string | undefined, mes_id: string, tools: string[], toolArgs: Map<string, Map<string, any>>) => {
        try{
            if (!conv_id) {
                throw Error("会話IDがありません")
            }
            if (!appStateContext?.state?.currentChat) {
                throw Error("会話が見つかりません")
            }
            // メッセージを削除した後に新たなリクエストを送り生成
            const currentConv = {...appStateContext?.state?.currentChat}
            const mesIdx = currentConv.messages.findIndex((v) => v.id === mes_id)
            if (mesIdx === -1) {
                throw Error("メッセージが見つかりません")
            }
            const deleteTarget = currentConv.messages.slice(mesIdx)
            for (const deleteMes of deleteTarget){
                if (deleteMes.id === undefined) continue;
                const deleteRes = await historyDelete(conv_id, deleteMes.id)
                if (!deleteRes.ok) {
                    throw Error("メッセージの削除に失敗しました")
                }
            }
            // mesIdx以降のすべてのメッセージを破棄
            currentConv.messages = currentConv.messages.slice(0, mesIdx)
            appStateContext?.dispatch({type: "UPDATE_CHAT_HISTORY", payload: currentConv })
            appStateContext?.dispatch({type: "UPDATE_CURRENT_CHAT", payload: currentConv })
            const promptTemplateOverride = appStateContext?.state.promptTemplateOverride?.id ?? undefined
            await makeApiRequestWithCosmosDB(currentConv, question, [], appStateContext?.state.selectedRAGIds, tools, toolArgs, promptTemplateOverride)
        } catch(e) {
            await showDialog("エラー", String(e), false);
        }
    }

    const parseCitationFromMessage = (message: ChatMessage) => {
        if (message?.role && message?.role === "tool") {
            try {
                const toolMessage = JSON.parse(message.content) as ToolMessageContent;
                return toolMessage.citations;
            }
            catch {
                return [];
            }
        }
        return [];
    }

    const disabledNewChatButton = () => {
        return (messages && messages.length === 0) || appStateContext?.state.chatHistoryLoadingState === ChatHistoryLoadingState.Loading
    }
    
    // 添付ファイルボタンの使用可否 false:使用可 true:使用不可
    const disabledFileAttachButton = () => {
        if (isCheckingOrLoadingThisConv || appStateContext?.state.chatHistoryLoadingState === ChatHistoryLoadingState.Loading){
            // ローディング中は添付ファイルボタンを使用できない
            return true;
        } else if (isConvDifferentUsers) {
            // 違うユーザーの会話の場合ボタンは使用できない
            return true;
        }
        return false
    }

    // 添付ファイルボタンの使用可否 false:使用可 true:使用不可
    const disabledTranscribeButton = () => {
        if (isCheckingOrLoadingThisConv || appStateContext?.state.chatHistoryLoadingState === ChatHistoryLoadingState.Loading){
            // ローディング中は添付ファイルボタンを使用できない
            return true;
        } else if (isConvDifferentUsers) {
            // 違うユーザーの会話の場合ボタンは使用できない
            return true;
        } else if(appStateContext?.state.selectedRAGIds.size != 0) {
            // RAG選択中は使用できない
            return true;
        }
        return false
    }


    useEffect(() => {
        if (disabledFileAttachButton()){
            // 添付ファイルクリア
            if ( files && files.length > 0 ){
                files.forEach((file, index) => {
                    removeFile(index);
                });
            }
        }

    },[disabledFileAttachButton])

    // ファイル選択
    const handleFiles = async (newFiles: File[], detectAssistantsApi: boolean = true) => {
        let fileCount = messages.filter((message) => message.contentType && message.contentType !== "").length;
        for (let i = 0; i < newFiles.length; i++) {
            const file = newFiles[i];
            // Const.MAX_FILE_SIZE MBより大きいファイルはアップロードできない
            if (file.size > MAX_FILE_SIZE * 1024 * 1024) {
                var maxSizeStr = MAX_FILE_SIZE >= 1024 ? MAX_FILE_SIZE / 1024 + "GB" : MAX_FILE_SIZE + "MB";
                await showDialog("エラー", maxSizeStr + "より大きいファイルはアップロードできません。\n ファイル名：" + file.name, false);
                continue;
            }
            // 5個より多いファイルはアップロードできない
            fileCount++;
            if (fileCount > MAX_FILE_COUNT) {
                await showDialog("エラー", MAX_FILE_COUNT + "個より多いファイルはアップロードできません。", false);
                break;
            }

            files.push(file);
        };
        const filesInfo = files.map(file => ({
            name: file.name,
            type: file.type,
        }));
        setFiles([...files])
        setFilesInfo(filesInfo);
        setFiles([...files]);
        if (detectAssistantsApi) {
            const assistants_api_file_type = ["text/csv", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"]
            const assistants_api_file_ext = [".csv", ".xlsx"]
            const should_use_code_interpreter = files.some(v => assistants_api_file_type.includes(v.type)) || appStateContext?.state.currentChat?.messages.some(v => v.fileName && assistants_api_file_ext.some(ext => v.fileName?.endsWith(ext)))
            if(should_use_code_interpreter) {
                setTools([...tools, "CodeInterpreter"])
            }
            else {
                setTools(tools.filter(t => t != "CodeInterpreter"))
            }
        }
        if (fileInputRef.current) {
            // input file クリア
            fileInputRef.current.value = '';
        }
    }
    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement> | React.DragEvent<HTMLDivElement>) => {
        let fileCount = messages.filter((message) => message.contentType && message.contentType !== "").length;
        fileCount += files.length;
        const newFiles = event.type === "drop" ? (event as React.DragEvent<HTMLDivElement>).dataTransfer.files : (event.target as HTMLInputElement).files;
        if (!newFiles) return;
        handleFiles(Array.from(newFiles))
    };

    // ファイル削除
    const removeFile = (indexToRemove :number) => {
        setFiles(files.filter((_, index) => index !== indexToRemove));
        setFilesInfo(filesInfo.filter((_, index) => index !== indexToRemove));
    };

    const openFileDialog = () => {
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    };
    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
      if (disabledFileAttachButton()) {
        event.preventDefault();  // 添付ファイルボタンが無効な場合、ドラッグを無効化
        return;  // 処理を終了
      }
      event.preventDefault();
      setIsDragOver(true);
    };
    
    const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
      if (disabledFileAttachButton()) {
        event.preventDefault();  // 添付ファイルボタンが無効な場合、ドロップを無効化
        return;  // 処理を終了
      }
      event.preventDefault();
      setIsDragOver(false);
      await handleFileChange(event);
    };
    
    const handleDragLeave = () => {
      setIsDragOver(false);
    };
    

    const [question, setQuestion] = useState<string>("");

    /** 添付ファイルボタンのアイテム一覧 */
    const attachButtonMenuProps: IContextualMenuProps = {
        items: [
            {
                key: 'transcription',
                ariaLabel: "音声文字起こし",
                disabled: disabledTranscribeButton(),
                onClick: setIsTranscriptionOpen.bind(null, true),
                onRenderContent: () => (
                    <Stack horizontal verticalAlign="center">
                        <NotepadEdit16Regular style={{ width: "20px", height: "20px", marginRight: "8px" }} />
                        <span>議事録作成</span>
                    </Stack>
                ),
            },
            {
                key: 'delete',
                ariaLabel: "ファイル添付",
                disabled: disabledFileAttachButton(),
                onClick: openFileDialog,
                onRenderContent: () => (
                    <Stack horizontal verticalAlign="center">
                        <Attach12Regular style={{ width: "20px", height: "20px", marginRight: "8px" }} />
                        <span>ファイル添付</span>
                    </Stack>
                ),
            },
        ],
    };

    return (
      <div className={`${styles.container} ${isDragOver && !disabledFileAttachButton() ? styles.dragOver : ""}`} role="main"
      onDragOver={(e) => {
      if (!disabledFileAttachButton()) {
        handleDragOver(e);
      }}}
      onDrop={(e) => {
      if (!disabledFileAttachButton()) {
        handleDrop(e);
      }}}
      onPaste={(e) => {
        if (!disabledFileAttachButton()) {
          const files = Array.from(e.clipboardData.files)
          handleFiles(files)
        }
      }}
      onDragLeave={handleDragLeave}>
            {showAuthMessage ? (
                <Stack className={styles.chatEmptyState}>
                    <ShieldLockRegular className={styles.chatIcon} style={{color: 'darkorange', height: "200px", width: "200px"}}/>
                    <h1 className={styles.chatEmptyStateTitle}>Authentication Not Configured</h1>
                    <h2 className={styles.chatEmptyStateSubtitle}>
                        This app does not have authentication configured. Please add an identity provider by finding your app in the 
                        <a href="https://portal.azure.com/" target="_blank"> Azure Portal </a>
                        and following 
                         <a href="https://learn.microsoft.com/en-us/azure/app-service/scenario-secure-app-authentication-app-service#3-configure-authentication-and-authorization" target="_blank"> these instructions</a>.
                    </h2>
                    <h2 className={styles.chatEmptyStateSubtitle} style={{fontSize: "20px"}}><strong>Authentication configuration takes a few minutes to apply. </strong></h2>
                    <h2 className={styles.chatEmptyStateSubtitle} style={{fontSize: "20px"}}><strong>If you deployed in the last 10 minutes, please wait and reload the page after 10 minutes.</strong></h2>
                </Stack>
            ) : (
                <Stack horizontal className={styles.chatRoot}>
                    <SideBar 
                        isOpen={appStateContext?.state.isSideBarOpen ?? false}
                        isOverlay={isMobile}
                        onClose={() => appStateContext?.dispatch({type: "SET_SIDEBAR_OPEN", payload: false})}
                        isRAGAvailable={appStateContext?.state.isRAGEnabled ?? false}
                        selectedKey={appStateContext?.state.sideBarState ?? "ChatHistory"}
                        onTabChange={(key) => appStateContext?.dispatch({type: "SET_SIDEBAR_STATE", payload: key})}
                        promptTemplateMenuProps={{
                            setQuestion: setQuestion,
                            handleFiles: handleFiles,
                            clearCurrentChat: newChat, 
                            onSend:(question, files, template_id) => {
                                const conversation = appStateContext?.state.currentChat ?? undefined
                                makeApiRequestWithCosmosDB(conversation, question, files, undefined, undefined, undefined, template_id) 
                            }
                        }}
                    />
                    <Stack className={`${styles.chatContainer} ${appStateContext?.state?.isRAGEnabled ? styles.chatContainer_RAG : ""}`}>
                        {   
                            isChatLoading ?  (
                                <Stack className={styles.chatEmptyState}>
                                    <Spinner size={3} />
                                </Stack>
                            ) :
                                (!mergedMessages || mergedMessages.length < 1 ? (
                                <Stack className={styles.chatEmptyState}>
                                    <img
                                        src={imageUrl}
                                        className={styles.chatIcon}
                                        aria-hidden="true"
                                    />
                                </Stack>
                            ) : (
                                <div className={styles.chatMessageStream} style={{ marginBottom: isCheckingOrLoadingThisConv ? "40px" : "0px"}} role="log" data-testid="chat-message-stream">
                                    {mergedMessages.map((answer, index) => (
                                        <React.Fragment key={index}>
                                            {answer.role === "user" ? 
                                            isFileMessage(answer) ? 
                                            <UserFileMessage answer={answer}/>
                                            :
                                            <UserMessage 
                                                answer={answer} 
                                                isEditable={!isFileMessage(answer) && isLatestUserMessage(messages, answer) && !isConvDifferentUsers && !isCheckingOrLoadingThisConv && !isSyncingThisConv} 
                                                isCopyable={!isFileMessage(answer)}
                                                conversation_id={appStateContext?.state.currentChat?.id}
                                                onEditSend={onEditSend}
                                            /> 
                                            : (
                                                answer.role === "assistant" ? 
                                                <div className={styles.chatMessageGpt} data-testid="assistant-message">
                                                        {isFileMessage(answer) ? (
                                                            <FileDisplay
                                                                fileName={answer.fileName ? answer.fileName : ""}
                                                                blobFileName={answer.content}
                                                                fileAreaMaxHeight="700px"
                                                                imgFileAlert="AIが生成する画像は、著作権に十分配慮したうえでご利用ください。"
                                                            />
                                                        ) : (
                                                            <Answer
                                                                answer={{
                                                                    answer: answer.content,
                                                                    citations: parseCitationFromMessage(mergedMessages[index - 1]),
                                                                    assistantFileMessages: mergedMessages.filter((message) => message.role == "assistant" && message.contentType && message.contentType == "file"),
                                                                    }}
                                                                refInfo={appStateContext?.state.currentChat?.id ? {conv: appStateContext?.state.currentChat, mesId: answer.id}: undefined}
                                                                onCitationClicked={c => onShowCitation(c)}
                                                            />
                                                        )}
                                                </div> 
                                                : null
                                            )}
                                        </React.Fragment>
                                    ))}
                                    {(isCheckingThisConv || (isGeneratingThisConv && !generatingMessages.has(thisConvId))) && (
                                        <>
                                            <div className={styles.chatMessageGpt} data-testid="assistant-message-loading">
                                                <Answer
                                                    answer={{
                                                        answer: thisTaskId ? "回答を生成中です..." : "質問を送信中です...",
                                                        citations: []
                                                    }}
                                                    onCitationClicked={() => null}
                                                    isLoading={true}
                                                />
                                            </div>
                                        </>
                                    )}
                                    <div ref={chatMessageStreamEnd} />
                                </div>
                            )) 
                        }

                        {/* Chat Input */}
                        <Stack horizontal className={styles.chatInput}>
                            {/* New Chat and Attach File Buttons */}
                            <Stack tokens={{childrenGap: "12px"}} horizontalAlign="center" verticalAlign="end">
                                <TooltipHost
                                    content="新しいチャットを開始" 
                                    directionalHint={DirectionalHint.topAutoEdge}
                                    delay={0}
                                    calloutProps={{ gapSpace: 0 }}
                                    styles={{ 
                                        root: { display: 'inline-block' } 
                                    }}
                                >
                                    {appStateContext?.state.isCosmosDBAvailable?.status !== CosmosDBStatus.NotConfigured ? (
                                        <ChatActionButton
                                            iconProps={{ iconName: 'Label', style: { fontSize: '20px' } }}
                                            onClick={newChat}
                                            disabled={disabledNewChatButton()}
                                            aria-label="start a new chat button"
                                        />
                                    ) : (
                                        <div></div>
                                    )}
                                </TooltipHost>
                                <input type="file" multiple onChange={handleFileChange} id="file-input" style={{ display: 'none' }} ref={fileInputRef} />
                                <ChatActionButton
                                    iconProps={{ iconName: "Add" }}
                                    menuProps={attachButtonMenuProps}
                                    aria-label="attach file button"
                                />
                                <div className={styles.version}>Ver.{VERSION}</div>
                            </Stack>
                                <MessageDialog
                                    hidden={hideMessageDialog}
                                    onDismiss={dialogHandlers.handleCancel}
                                    onConfirm={dialogHandlers.handleOk}
                                    dialogContentProps={messageDialogContentProps}
                                    showCancelButton={showCancelButton}
                                >
                                </MessageDialog>
                                <MinutesTranscriptionDialog isOpen={isTranscriptionOpen} setIsOpen={setIsTranscriptionOpen} />
                            <Stack style={{flex: 1, minWidth: 0, justifyContent: "end"}}>
                                {thisTaskId && (
                                    <Stack 
                                        horizontal
                                        className={styles.stopGeneratingContainer}
                                        role="button"
                                        aria-label="Stop generating"
                                        data-testid="stop-generating"
                                        tabIndex={0}
                                        onClick={async () => {
                                            await stopGenerating(thisTaskId)
                                        }}
                                        onKeyDown={async(e) => {
                                            if (e.key === "Enter" || e.key === " "){
                                                await stopGenerating(thisTaskId)
                                            }
                                        }}
                                        >
                                            <SquareRegular className={styles.stopGeneratingIcon} aria-hidden="true"/>
                                            <span className={styles.stopGeneratingText} aria-hidden="true">回答生成中... {!isMobile && "中止するにはこちらをクリック"}</span>
                                    </Stack>
                                )}
                                {filesInfo.length > 0 && (
                                    <Stack horizontal className={styles.fileUploadContainer}>
                                        {filesInfo.map((file, index) => (
                                            <FileIcon key={index} contentType={file.type} name={file.name} onRemoveFile={() => removeFile(index)} testId="attached-file" />
                                        ))}
                                    </Stack>
                                )} 
                                <QuestionInput
                                    question={question} // questionを渡す
                                    setQuestion={setQuestion} // setQuestionを渡す
                                    clearOnSend
                                    placeholder= {(appStateContext?.state?.isRAGEnabled ? "<文書検索モード>" : "<通常チャットモード>") + "質問を入力してください"}
                                    disabled={isConvDifferentUsers}
                                    sendNotAllowed={isCheckingOrLoadingThisConv || isChatLoading || isSyncingThisConv}
                                    onSend={(question, id, tools, toolArgs) => {
                                        // プロンプトテンプレートによるオーバーライド
                                        const promptTemplateOverride = appStateContext?.state.promptTemplateOverride?.id ?? undefined
                                        const conversation = appStateContext?.state.currentChat ?? undefined
                                        makeApiRequestWithCosmosDB(conversation, question, files, appStateContext?.state.selectedRAGIds, tools, toolArgs, promptTemplateOverride)
                                    }}
                                    conversationId={appStateContext?.state.currentChat?.id}
                                    files={files}
                                    isFileSelected={0 < files.length}
                                    messages={messages}
                                    maxHeight="240px"
                                    tools={tools}
                                    setTools={setTools}
                                    toolArgs={toolArgs}
                                    setToolArgs={setToolArgs}
                                />
                            </Stack>
                        </Stack>
                    </Stack>
                    {/* Citation Panel */}
                    {!isMobile && mergedMessages && mergedMessages.length > 0 && isCitationPanelOpen && activeCitation && (
                        <CitationPanel
                            activeCitation={activeCitation}
                            onClose={() => setIsCitationPanelOpen(false)}
                            onViewSource={onViewSource}
                            urlBlobMap={urlBlobMap_render}
                            loadingBlobs={loadingBlobs_render}
                        />
                    )}
                    {isMobile && mergedMessages && mergedMessages.length > 0 && activeCitation && (
                        <MobileCitationPanel
                            activeCitation={activeCitation}
                            isOpen={isCitationPanelOpen}
                            onClose={() => setIsCitationPanelOpen(false)}
                            onViewSource={onViewSource}
                            urlBlobMap={urlBlobMap_render}
                            loadingBlobs={loadingBlobs_render}
                        />
                    )}
                </Stack>
            )}
        </div>
    );
};

export default Chat;
